'use client';

import {
  useCallback,
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { database } from '@/_libs/firebase';
import { ref, onValue } from 'firebase/database';
import {
  getMessages,
  markConversationSeen,
  markThreadSeen,
  sendMessage as APIsendMessage,
} from '@/_api/client-conversations';
import { getV2Conversations } from '@/_api/v2/conversations';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { markAsUnseen } from '@/_api/admin-conversations';
import type { ClientConversationsProviderProps } from './ClientConversationsProvider.types';
import type { Conversation } from '@/_types/models/conversations';

const DEBOUNCE_TIMEOUT = 1000 as const;

const ClientConversationsContext =
  createContext<ClientConversationsProviderProps>({
    clearConversations: () => null,
    conversationIdNumber: undefined,
    conversations: [],
    currentEntityUnseenCount: 0,
    entityUnseenCounts: {},
    isLoading: false,
    isMessagesLoading: false,
    isSending: false,
    reloadConversations: () => null,
    reloadMessages: () => null,
    markConversationAsUnseen: () => null,
    selectedMessage: undefined,
    sendMessage: (_text: string) => undefined,
    setSelectedMessage: () => null,
    setThreadSeen: (_conversationId: number, _threadId: number) =>
      Promise.resolve(),
    unseenCount: 0,
  });

const ClientConversationsProvider: any = ({ children }: any) => {
  const { conversationId } = useParams();
  const { selectedHash, refreshCompanyConversations } =
    useContext(CompanyContext);
  const { selectedCompanyHash } = useContext(UserContext);
  const { push } = useRouter();

  const { user, token } = useContext(UserContext);
  const conversationSearchParam = useSearchParams()?.get('conversationId');

  const conversationIdNumber: number | undefined = useMemo(
    () =>
      conversationId || conversationSearchParam
        ? parseInt(
            (conversationId as string) || (conversationSearchParam as string),
          )
        : undefined,
    [conversationId, conversationSearchParam],
  );

  const [isMessagesLoading, setMessagesLoading] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isSending, setSending] = useState<boolean>(false);
  const [conversations, setConversations] = useState<Array<Conversation>>([]);
  const [selectedMessage, setSelectedMessage] = useState<
    Conversation | undefined
  >(undefined);

  const unseenCount: number = useMemo(
    () =>
      !!conversations?.length && Array.isArray(conversations)
        ? conversations?.reduce((total, conversation) => {
            const unreadInConversation = conversation.unseen_messages
              ?.filter((m) => !m?.is_log)
              .reduce(
                (count, message) =>
                  message.unseen_user_ids.includes(user?.id!)
                    ? count + 1
                    : count,
                0,
              );
            return total + unreadInConversation;
          }, 0)
        : 0,
    [conversations, user?.id],
  );

  const markConversationAsUnseen = useCallback(
    (conversationId: number) => {
      markAsUnseen({ id: conversationId, token });
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation && conversation.last_message) {
        const lastMessage = conversation.last_message;
        if (lastMessage && !lastMessage.unseen_user_ids.includes(user?.id!)) {
          lastMessage.unseen_user_ids.push(user?.id!);
        }
        setConversations([...conversations]);
      }
    },
    [token, conversations, user?.id],
  );

  const entityUnseenCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {};

    conversations.forEach((conversation) => {
      const companyHash = conversation.relevant_company_hash_id;
      if (!companyHash) return;

      const unseenInConversation =
        conversation.unseen_messages
          ?.filter((m) => !m?.is_log)
          .reduce(
            (count, message) =>
              message.unseen_user_ids.includes(user?.id!) ? count + 1 : count,
            0,
          ) ?? 0;

      counts[companyHash] = (counts[companyHash] || 0) + unseenInConversation;
    });

    return counts;
  }, [conversations, user?.id]);

  const currentEntityUnseenCount: number = useMemo(
    () =>
      selectedCompanyHash ? entityUnseenCounts[selectedCompanyHash] || 0 : 0,
    [entityUnseenCounts, selectedCompanyHash],
  );

  const sendMessage = async (
    text: string,
    selectedMessageId?: number | null,
    date?: string | null,
    messageTag?: string,
    hasReminder?: boolean,
  ): Promise<void> => {
    if (!text) return;

    setSending(true);

    try {
      const baseParams = {
        conversationId: selectedMessage?.id ?? (conversationIdNumber as number),
        text,
        messageTag: messageTag ?? 'general',
        hasReminder: hasReminder ?? false,
        messageThreadId: selectedMessageId || undefined,
        scheduledTime: date || null,
      };

      await APIsendMessage({
        ...baseParams,
        token,
      });
      await loadMessages(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const loadMessages = useCallback(
    async (isSilent?: boolean) => {
      if ((!conversationIdNumber && !selectedHash) || !token) return;
      if (!isSilent) setSelectedMessage(undefined);

      try {
        if (!isSilent) setMessagesLoading(true);

        const conversations = await getV2Conversations({ token });

        setConversations(conversations);
        refreshCompanyConversations(undefined, true);

        if (conversationIdNumber) {
          const conversation = await getMessages({
            conversationId: conversationIdNumber,
            token,
          });

          const sortedConversationWithMessages = {
            ...conversation,
            messages: conversation.messages?.sort((a, b) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return dateA - dateB;
            }),
          };

          setSelectedMessage(sortedConversationWithMessages);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setMessagesLoading(false);
      }
    },
    [conversationIdNumber, selectedHash, refreshCompanyConversations, token],
  );

  const updateUnseenMessages = useCallback(
    (conversationId: number, threadId?: number) => {
      setConversations((prevConversations) =>
        prevConversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;

          return {
            ...conversation,
            messages: conversation.messages?.map((message) => {
              if (threadId && message.message_thread_id !== threadId) {
                return message;
              }

              return {
                ...message,
                unseen_user_ids: message.unseen_user_ids.filter(
                  (id) => id !== user?.id,
                ),
              };
            }),
            unseen_messages: conversation.unseen_messages?.map((message) => {
              if (threadId && message.message_thread_id !== threadId) {
                return message;
              }

              return {
                ...message,
                unseen_user_ids: message.unseen_user_ids.filter(
                  (id) => id !== user?.id,
                ),
              };
            }),
          };
        }),
      );

      setSelectedMessage((prev) => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages?.map((message) => {
            if (threadId && message.message_thread_id !== threadId) {
              return message;
            }
            return {
              ...message,
              unseen_user_ids: message.unseen_user_ids.filter(
                (id) => id !== user?.id,
              ),
            };
          }),
          unseen_messages: prev.unseen_messages?.map((message) => {
            if (threadId && message.message_thread_id !== threadId) {
              return message;
            }
            return {
              ...message,
              unseen_user_ids: message.unseen_user_ids.filter(
                (id) => id !== user?.id,
              ),
            };
          }),
        };
      });
    },
    [user?.id],
  );

  const setConversationSeen = useCallback(
    async (conversationId: number) => {
      if (!conversationId || !token) return;

      try {
        await markConversationSeen({
          conversationId,
          token,
        });
        updateUnseenMessages(conversationId);
      } catch (error) {
        console.error(error);
      }
    },
    [token, updateUnseenMessages],
  );

  const setThreadSeen = useCallback(
    async (conversationId: number, threadId: number) => {
      if (!conversationId || !threadId || !token) return;

      try {
        await markThreadSeen({
          conversationId,
          threadId,
          token,
        });
        updateUnseenMessages(conversationId, threadId);
        loadMessages(true);
      } catch (error) {
        console.error(error);
      }
    },
    [token, updateUnseenMessages, loadMessages],
  );

  const reloadConversations = useCallback(
    async (isSilent: boolean = false) => {
      try {
        if (!isSilent) setLoading(true);
        const conversations = await getV2Conversations({ token });

        if (conversations?.error) return;

        setConversations(conversations);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const orderedConversations: Array<Conversation> = useMemo(
    () =>
      Array.isArray(conversations)
        ? [...conversations]
            .filter((convo) => convo.last_message_datetime)
            .sort(
              (a, b) =>
                new Date(b?.last_message_datetime || '').getTime() -
                new Date(a?.last_message_datetime || '').getTime(),
            )
            .concat(
              // simply move all empty conversations to the end
              [...conversations].filter(
                (convo) => !convo.last_message_datetime,
              ),
            )
        : [],
    [conversations],
  );

  const clearConversations = useCallback((): void => {
    if (typeof selectedMessage?.id === 'number') {
      setSelectedMessage(undefined);
    }
  }, [selectedMessage?.id]);

  useEffect(() => {
    conversationIdNumber && setConversationSeen(conversationIdNumber);
  }, [token, loadMessages, setConversationSeen, conversationIdNumber]);

  useEffect(() => {
    selectedHash && loadMessages();
  }, [selectedHash, loadMessages]);

  useEffect(() => {
    if (conversationSearchParam) {
      push(`/admin/peak/messages/${conversationSearchParam}`);
    }
  }, [conversationSearchParam, push]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let unsubscribe: (() => void) | undefined;

    const loadConversations = async (): Promise<void> => {
      if (!token || !user?.id) return;

      try {
        const conversations = await getV2Conversations({ token });

        if (!conversations?.error) {
          setConversations(conversations);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const startRealtimeListener = (): void => {
      if (!token || !user?.id) return;

      const messagesRef = ref(database, `users/${user.id}/newMessages`);
      const handleUpdate = async (_snapshot: any): Promise<void> => {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(async () => {
          await loadConversations();
          await loadMessages(true);
        }, DEBOUNCE_TIMEOUT);
      };

      unsubscribe = onValue(messagesRef, handleUpdate);
    };

    token && startRealtimeListener();

    return () => {
      clearTimeout(timeoutId);

      unsubscribe && unsubscribe();
    };
  }, [loadMessages, token, conversationIdNumber, user?.id]);

  return (
    <ClientConversationsContext
      value={{
        clearConversations,
        conversationIdNumber,
        conversations: orderedConversations,
        currentEntityUnseenCount,
        entityUnseenCounts,
        isMessagesLoading,
        isLoading,
        isSending,
        reloadConversations,
        markConversationAsUnseen,
        reloadMessages: loadMessages,
        selectedMessage,
        sendMessage,
        setSelectedMessage,
        setThreadSeen,
        unseenCount,
      }}
    >
      {children}
    </ClientConversationsContext>
  );
};

export { ClientConversationsContext, ClientConversationsProvider };
